import { GetScannedProductRes } from "gen/api/product/service_pb"
import { Product as CatalogProduct } from "gen/catalog/models_pb"
import {
  AdditionalFees_Fee as AdditionalFee,
  Location,
  PricingTiers,
  ProductCategory,
} from "gen/company/models_pb"
import { LedgerEntry_UomSnapshot } from "gen/ledger/models_pb"
import { Tier } from "gen/price/models_pb"
import { Inventories, Product } from "gen/product/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { SearchProductsV2Res_Result as SearchProductsV2Result } from "gen/search/service/service_pb"
import { ProductSalesAnalytics } from "gen/txn/models_pb"
import { Vendor } from "gen/vendors/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { UUID } from "shared-ts/classes/uuid"

export interface TransactionProduct {
  product?: Product
  matchedPriceProduct?: Product
  matchedCostProduct?: Product
  inventories?: Inventories
  primaryCostVendor?: Vendor
  additionalFees?: AdditionalFee[]
  catalogProduct?: CatalogProduct
  analytics?: ProductSalesAnalytics
  department?: ProductCategory
  class?: ProductCategory
  fineline?: ProductCategory
  tierPrices?: Tier[]
  locationsById?: Record<string, Location>
  pricingTiers?: PricingTiers
  delta: Decimal
  quantity: Decimal
  isScan: boolean
  uomId?: PBUUID
  uomSnapshot?: LedgerEntry_UomSnapshot
  templateSaleProductNotes?: string
  kit?: TransactionKit
}

export interface TransactionKit {
  // A component can't have its own components: kits are one level deep
  components: Omit<TransactionProduct, "kit">[]
}

export const convertSearchProductResToTransactionProduct = (
  searchRes: SearchProductsV2Result
): TransactionProduct => ({
  product: searchRes?.product,
  matchedPriceProduct: searchRes?.matchedPriceProduct,
  matchedCostProduct: searchRes?.matchedCostProduct,
  catalogProduct: searchRes?.catalogProduct,
  inventories: searchRes?.inventories,
  additionalFees: searchRes?.additionalFees,
  primaryCostVendor: searchRes?.primaryCostVendor,
  analytics: searchRes?.analytics,
  department: searchRes?.department,
  class: searchRes?.class,
  fineline: searchRes?.fineline,
  tierPrices: searchRes?.tierPrices,
  delta: searchRes?.deltaV2
    ? Decimal.fromPB(searchRes.deltaV2)
    : Decimal.zero(),
  quantity: Decimal.zero(),
  isScan: false,
  uomId: undefined,
  uomSnapshot: undefined,
  templateSaleProductNotes: searchRes.product?.templateSaleProductNotes,
})

export const convertSearchProductsToTransactionProducts = (
  searchRes: SearchProductsV2Result[]
): TransactionProduct[] =>
  searchRes?.map((r) => convertSearchProductResToTransactionProduct(r))

export const convertScanProductResToTransactionProduct = (
  scanRes: GetScannedProductRes,
  pricingRegionId: PBUUID | undefined,
  showPricingRegion: boolean
): TransactionProduct => ({
  product: scanRes?.product,
  matchedPriceProduct: scanRes?.matchedPriceProduct,
  matchedCostProduct: scanRes?.matchedCostProduct,
  inventories: scanRes?.inventories,
  additionalFees: scanRes?.additionalFees,
  primaryCostVendor: scanRes?.primaryCostVendor,
  locationsById: scanRes?.locationsById,
  pricingTiers: scanRes?.pricingTiers,
  tierPrices: scanRes?.regionTierPrices.find((r) =>
    showPricingRegion && pricingRegionId
      ? UUID.eqFromPB(r.pricingRegion?.id, pricingRegionId)
      : r.pricingRegion?.isDefault
  )?.tierPrices,
  delta: Decimal.zero(),
  quantity: scanRes?.quantityV2
    ? Decimal.fromPB(scanRes.quantityV2)
    : Decimal.zero(),
  isScan: true,
  uomId: scanRes?.uomId,
  uomSnapshot: undefined,
  templateSaleProductNotes: scanRes?.product?.templateSaleProductNotes,
})
